/**
 * Notify Me! Wishlist — The Forme (proxy bridge).
 *
 * Notify Me! App Embed owns wishlist state, persistence, toast and the
 * /pages/my-wishlist page. The Forme hearts / PDP CTA stay the visible
 * controls (look & feel unchanged); this bridge pairs each Forme button
 * with the Notify button the SDK injected for the same product, proxies
 * clicks to it, and mirrors state back onto the Forme button.
 *
 * Pairing:
 *   PDP CTA (data-wishlist-scope="variant") → .nm-PRODUCT_PAGE-wishlist-button
 *   Tile hearts (product scope)             → .nm-wishlist-button inside the
 *                                             same product tile / card
 * Paired Notify buttons get `.tf-nm-paired` (hidden by notify-wishlist.css).
 * Notify buttons with no Forme counterpart stay visible as a fallback.
 *
 * State mirror: `NM_customerData` (the SDK's local wishlist mirror) is the
 * primary source; the paired Notify button's filled-heart / label state is
 * the fallback for signed-in shoppers. Header count mirrors the SDK count
 * badge. Notify has no documented public JS SDK for custom buttons — if
 * Notify ships one, replace the proxy click with direct SDK calls.
 */
(function () {
  "use strict";

  var BUTTON_SELECTOR = "[data-wishlist-button]";
  // PDP buttons are `.nm-wishlist-button`; collection / landing card
  // buttons are `.nm-wishlist-on-card-button` (different host class).
  var NM_BUTTON_SELECTOR =
    ".nm-wishlist-button, .nm-wishlist-on-card-button";
  var NM_PRODUCT_PAGE_SELECTOR = ".nm-PRODUCT_PAGE-wishlist-button";
  var ACTIVE_CLASS = "ui-wishlist-button--active";
  var PAIRED_CLASS = "tf-nm-paired";
  var PDP_FORM_SELECTOR = "[data-pdp-form]";
  var VARIANT_INPUT_SELECTOR = "[data-pdp-form-variant-input]";
  var TILE_SELECTOR =
    "[data-standard-product-tile], [data-featured-product-tile], " +
    "[data-spotlight-tile], article, li";

  // productId -> true, rebuilt from NM_customerData on every sync.
  var wishlistedByProduct = Object.create(null);
  var wishlistStateKnown = false;
  var syncScheduled = false;
  var pendingByBtn = typeof WeakSet !== "undefined" ? new WeakSet() : null;

  function normalizeId(value) {
    if (value === null || value === undefined || value === "") return "";
    return String(value);
  }

  function buttonProductId(btn) {
    return normalizeId(
      btn.getAttribute("data-wishlist-product-id") ||
        btn.getAttribute("data-product-id") ||
        "",
    );
  }

  function isVariantScoped(btn) {
    if (!btn) return false;
    var scope = btn.getAttribute("data-wishlist-scope");
    if (scope) return scope === "variant";
    return !!(btn.closest && btn.closest(PDP_FORM_SELECTOR));
  }

  /* ── Notify state ─────────────────────────────────────────────────────── */

  function readNotifyState() {
    wishlistedByProduct = Object.create(null);
    wishlistStateKnown = false;

    var raw = null;
    try {
      raw = window.localStorage.getItem("NM_customerData");
    } catch (err) {
      raw = null;
    }
    if (!raw) return;

    try {
      var data = JSON.parse(raw);
      var lists = (data && data.wishlists) || [];
      for (var i = 0; i < lists.length; i++) {
        var items = (lists[i] && lists[i].items) || [];
        for (var j = 0; j < items.length; j++) {
          var pid = normalizeId(items[j] && items[j].product_id);
          if (pid) wishlistedByProduct[pid] = true;
        }
      }
      wishlistStateKnown = true;
    } catch (err2) {
      /* keep unknown — paired-button fallback below still syncs hearts */
    }
  }

  function notifyCount() {
    var count = 0;
    for (var key in wishlistedByProduct) {
      if (Object.prototype.hasOwnProperty.call(wishlistedByProduct, key)) {
        count += 1;
      }
    }
    if (count > 0 || wishlistStateKnown) return count;

    var badge = document.querySelector(".nm-wishlist-count-badge");
    var badgeCount = badge ? parseInt(badge.textContent, 10) : NaN;
    return isNaN(badgeCount) ? 0 : badgeCount;
  }

  /** Active = filled heart (fill: currentcolor) or the "in wishlist" label. */
  function notifyButtonIsActive(nmBtn) {
    if (!nmBtn) return false;
    var path = nmBtn.querySelector("svg path");
    var style = path ? path.getAttribute("style") || "" : "";
    if (/fill:\s*currentcolor/i.test(style)) return true;

    var removeLabel = "";
    try {
      var contents =
        window._ReStockConfig &&
        window._ReStockConfig.wishlist &&
        window._ReStockConfig.wishlist.ui_contents;
      var lang =
        (window._ReStockConfig && window._ReStockConfig.default_language) ||
        "en";
      removeLabel =
        (contents && contents[lang] &&
          contents[lang].remove_from_wishlist_button_label) ||
        "";
    } catch (err) {
      removeLabel = "";
    }
    var text = (nmBtn.textContent || "").replace(/\s+/g, " ").trim();
    return !!removeLabel && text.indexOf(removeLabel) !== -1;
  }

  /* ── Pairing ──────────────────────────────────────────────────────────── */

  function findNotifyButton(btn) {
    if (isVariantScoped(btn)) {
      return document.querySelector(NM_PRODUCT_PAGE_SELECTOR);
    }

    var tile = btn.closest ? btn.closest(TILE_SELECTOR) : null;
    if (tile) {
      var inTile = tile.querySelector(NM_BUTTON_SELECTOR);
      if (inTile) return inTile;
    }

    // Walk up until an ancestor holds exactly one Notify button — that is
    // this product's card. Stop before ancestors holding several (grids).
    var node = btn.parentElement;
    for (var depth = 0; node && depth < 6; depth++) {
      var candidates = node.querySelectorAll(NM_BUTTON_SELECTOR);
      if (candidates.length === 1) return candidates[0];
      if (candidates.length > 1) return null;
      node = node.parentElement;
    }
    return null;
  }

  function pairNotifyButtons() {
    var buttons = document.querySelectorAll(BUTTON_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      var nmBtn = findNotifyButton(buttons[i]);
      if (nmBtn) nmBtn.classList.add(PAIRED_CLASS);
    }
  }

  /* ── Forme button / header sync ───────────────────────────────────────── */

  function setButtonState(btn, active) {
    if (!btn) return;
    btn.classList.toggle(ACTIVE_CLASS, active);
    btn.setAttribute("aria-pressed", active ? "true" : "false");
    var labelAdd = btn.getAttribute("data-wishlist-label-add") || "Add to wishlist";
    var labelRemove =
      btn.getAttribute("data-wishlist-label-remove") || "Remove from wishlist";
    btn.setAttribute("aria-label", active ? labelRemove : labelAdd);
  }

  function syncButton(btn) {
    if (!btn || btn.hasAttribute("disabled")) return;
    var pid = buttonProductId(btn);

    if (pid && wishlistStateKnown) {
      setButtonState(btn, !!wishlistedByProduct[pid]);
      return;
    }

    // Signed-in shoppers may not mirror into NM_customerData — fall back
    // to the paired Notify button's own active state.
    var nmBtn = findNotifyButton(btn);
    if (nmBtn) {
      setButtonState(btn, notifyButtonIsActive(nmBtn));
    }
  }

  function syncHeaderCount() {
    var n = notifyCount();
    var label = n > 99 ? "99+" : String(n);
    var links = document.querySelectorAll(
      '[data-header-additional-link-key="wishlist"]',
    );

    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var badge = link.querySelector("[data-wishlist-count]");
      if (!badge) {
        badge = document.createElement("span");
        badge.setAttribute("data-wishlist-count", "");
        badge.className = link.classList.contains("ui-header-icon")
          ? "ui-header-icon__count"
          : "ui-megamenu-mobile__wishlist-count";
        badge.setAttribute("aria-hidden", "true");
        link.appendChild(badge);
      }

      if (n > 0) {
        badge.textContent = label;
        badge.hidden = false;
        link.setAttribute("data-wishlist-has-count", "true");
      } else {
        badge.textContent = "";
        badge.hidden = true;
        link.removeAttribute("data-wishlist-has-count");
      }
    }
  }

  function syncAll() {
    readNotifyState();
    pairNotifyButtons();
    var buttons = document.querySelectorAll(BUTTON_SELECTOR);
    for (var i = 0; i < buttons.length; i++) syncButton(buttons[i]);
    syncHeaderCount();
  }

  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(function () {
      syncScheduled = false;
      syncAll();
    });
  }

  /** Notify settles state async (toast, badge, storage) — sync in waves. */
  function scheduleSyncWaves() {
    scheduleSync();
    window.setTimeout(scheduleSync, 350);
    window.setTimeout(scheduleSync, 1200);
  }

  /* ── Click proxy ──────────────────────────────────────────────────────── */

  function toggleWishlist(btn) {
    if (pendingByBtn && pendingByBtn.has(btn)) return;

    var nmBtn = findNotifyButton(btn);
    if (!nmBtn) {
      // Notify button not injected (yet) for this surface — e.g. Collection
      // / Home visibility still off in the Notify app. Never fake state.
      console.warn(
        "[tf.notify] No Notify Me wishlist button paired for this product. " +
          "Enable Wishlist → Settings → Visibility (Collection / Home) in " +
          "the Notify Me! app so its buttons are injected on this surface.",
        btn,
      );
      return;
    }

    if (pendingByBtn) pendingByBtn.add(btn);

    // Optimistic: match what the Notify click is about to do.
    var wasActive = btn.classList.contains(ACTIVE_CLASS);
    setButtonState(btn, !wasActive);

    nmBtn.click();

    window.setTimeout(function () {
      if (pendingByBtn) pendingByBtn.delete(btn);
      scheduleSyncWaves();
      document.dispatchEvent(
        new CustomEvent("wishlist:updated", { bubbles: true }),
      );
    }, 60);
  }

  /* ── Variant sync (PDP) ───────────────────────────────────────────────── */

  function syncVariantAttrs() {
    var buttons = document.querySelectorAll(BUTTON_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (!isVariantScoped(btn)) continue;
      var root = btn.closest(PDP_FORM_SELECTOR);
      if (!root) continue;
      var input =
        root.querySelector(VARIANT_INPUT_SELECTOR) ||
        root.querySelector('input[name="id"]');
      if (input && input.value) {
        btn.setAttribute("data-wishlist-variant-id", input.value);
        btn.setAttribute("data-variant-id", input.value);
      }
    }
  }

  /* ── Init ─────────────────────────────────────────────────────────────── */

  // Notify's wishlist page breaks out with `width: 100vw`, but 100vw
  // includes the scrollbar — the grid lands half a scrollbar left of the
  // theme gutter. Publish the real scrollbar width so notify-wishlist.css
  // can centre the breakout on the visible (client) width instead.
  function publishScrollbarWidth() {
    var width =
      window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      "--tf-scrollbar-width",
      width + "px",
    );
  }

  function init() {
    document.documentElement.classList.add("notify-bridge-ready");

    publishScrollbarWidth();
    window.addEventListener("resize", publishScrollbarWidth);

    scheduleSyncWaves();

    if (document.body) {
      var observer = new MutationObserver(function () {
        scheduleSync();
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    document.addEventListener(
      "click",
      function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== "function") return;
        var btn = target.closest(BUTTON_SELECTOR);
        if (!btn || btn.hasAttribute("disabled")) return;

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        toggleWishlist(btn);
      },
      true,
    );

    // Size / colour changes swap the selected variant — keep the PDP CTA's
    // variant attrs current and re-sync against the paired Notify button.
    document.addEventListener(
      "change",
      function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== "function") return;
        if (!target.closest(PDP_FORM_SELECTOR)) return;
        syncVariantAttrs();
        scheduleSync();
      },
      true,
    );

    // Returning from the wishlist page (or another tab) can change the list
    // without a click here — re-read on visibility / bfcache restore.
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "visible") scheduleSyncWaves();
    });
    window.addEventListener("pageshow", function (event) {
      if (event.persisted) scheduleSyncWaves();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
